import PermissionButton from 'components/buttons/PermissionButton.tsx';
import { useModal } from 'components/modals/useModal.ts';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useUpdateDebtCollectionMutation } from 'store/api/tenants.ts';
import { ClassifierData } from 'types/classifiers.ts';
import {
    AutoblockCriteriaForm,
    DebtCollectionUpdateDto,
    ReminderIntervalsForm,
    TenantConfigurationDto,
} from 'types/tenants.ts';
import { errorAlert } from 'utils/alert';
import { getClassifierText, getIntl, t } from 'utils/getTranslations.ts';
import { isNewDesignCopy } from 'utils/newDesignCopy';
import AutoblockCriteriaEditModal from './AutoblockCriteriaEditModal.tsx';
import { CONFIGURATION_CARD_CLASS, CONFIGURATION_CARD_HEADER_CLASS } from './configurationCard';
import ReminderIntervalsEditModal from './ReminderIntervalsEditModal.tsx';

interface Props {
    tenantConfiguration: TenantConfigurationDto;
    classifiers: Record<string, ClassifierData>;
}

const DebtCollection = ({ classifiers, tenantConfiguration }: Props): JSX.Element => {
    const { openModal, closeModal } = useModal();
    const [updateDebtCollection] = useUpdateDebtCollectionMutation();
    const {
        debtCollectionReminderIntervals,
        matterBlockingDebtClientThresholdAmount,
        matterBlockingGracePeriodDays,
        matterBlockingWarningPeriodDays,
    } = tenantConfiguration;

    const onDebtCollectionSubmit = useCallback(
        async (dto: DebtCollectionUpdateDto): Promise<void> => {
            try {
                updateDebtCollection(dto)
                    .unwrap()
                    .then(() => {
                        closeModal();
                    })
                    .catch(console.error);
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [closeModal, updateDebtCollection]
    );

    const openReminderIntervalsEditModal = useCallback((): void => {
        openModal(
            <ReminderIntervalsEditModal
                classifiers={classifiers}
                initialData={{
                    debtCollectionReminderIntervals: tenantConfiguration.debtCollectionReminderIntervals,
                }}
                onSubmit={(data) => {
                    void onDebtCollectionSubmit(mapReminderIntervalsToDto(tenantConfiguration, data));
                }}
                onClose={closeModal}
            />,
            closeModal
        );
    }, [classifiers, tenantConfiguration, onDebtCollectionSubmit, openModal, closeModal]);

    const openAutoblockCriteriaEditModal = useCallback((): void => {
        openModal(
            <AutoblockCriteriaEditModal
                initialData={{
                    matterBlockingDebtClientThresholdAmount:
                        tenantConfiguration.matterBlockingDebtClientThresholdAmount ?? null,
                    matterBlockingGracePeriodDays: tenantConfiguration.matterBlockingGracePeriodDays,
                    matterBlockingWarningPeriodDays: tenantConfiguration.matterBlockingWarningPeriodDays,
                }}
                onSubmit={(data) => {
                    void onDebtCollectionSubmit(mapAutoblockCriteriaToDto(tenantConfiguration, data));
                }}
                onClose={closeModal}
            />,
            closeModal
        );
    }, [tenantConfiguration, onDebtCollectionSubmit, openModal, closeModal]);

    return (
        <>
            <h3 className="text-h3 text-balance">
                <FormattedMessage id="configuration.debt-collection" />
            </h3>
            <div className="new:gap-(--card-gap) flex flex-col gap-4">
                <div className={CONFIGURATION_CARD_CLASS}>
                    <div className={CONFIGURATION_CARD_HEADER_CLASS}>
                        <FormattedMessage id="configuration.debt-reminder-intervals" />
                    </div>
                    <div className="flex p-4">
                        <div className="flex w-1/3 flex-col gap-2">
                            {debtCollectionReminderIntervals.map((interval) => (
                                <div className="flex items-center justify-between" key={interval.debtReminderType}>
                                    <p className="text-body2">
                                        {getClassifierText(
                                            classifiers['DEBT_REMINDER_TYPE'],
                                            interval.debtReminderType
                                        )}
                                    </p>
                                    <InputGroup className="bg-paper w-20">
                                        <InputGroupInput
                                            value={interval.days}
                                            disabled
                                            aria-label={getClassifierText(
                                                classifiers['DEBT_REMINDER_TYPE'],
                                                interval.debtReminderType
                                            )}
                                        />
                                        <InputGroupAddon align="inline-end">
                                            <span className="text-body2">{t('configuration.days')}</span>
                                        </InputGroupAddon>
                                    </InputGroup>
                                </div>
                            ))}
                        </div>
                        <div className="ml-10 flex items-center">
                            <PermissionButton
                                permissions={['UPDATE.BACK_OFFICE_SETTINGS']}
                                variant="outlined"
                                size="small"
                                className="h-8"
                                onClick={openReminderIntervalsEditModal}
                            >
                                <FormattedMessage id="edit" />
                            </PermissionButton>
                        </div>
                    </div>
                </div>
                <div className={CONFIGURATION_CARD_CLASS}>
                    <div className={CONFIGURATION_CARD_HEADER_CLASS}>
                        <FormattedMessage id="configuration.autoblock-criteria" />
                    </div>
                    <div className="flex p-4">
                        <div className="flex w-1/3 flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <p className="text-body2">
                                    <FormattedMessage id="configuration.debt-threshold-amount" />
                                </p>
                                {matterBlockingDebtClientThresholdAmount
                                    ? isNewDesignCopy()
                                        ? getIntl().formatNumber(matterBlockingDebtClientThresholdAmount)
                                        : matterBlockingDebtClientThresholdAmount.toLocaleString()
                                    : '-'}
                            </div>
                            <div className="flex items-center justify-between">
                                <p className="text-body2">
                                    <FormattedMessage id="configuration.debt-grace-period-days" />
                                </p>
                                {matterBlockingGracePeriodDays ? matterBlockingGracePeriodDays : '-'}
                            </div>
                            <div className="flex items-center justify-between">
                                <p className="text-body2">
                                    <FormattedMessage id="configuration.autoblock-warning-days" />
                                </p>
                                {matterBlockingWarningPeriodDays ? matterBlockingWarningPeriodDays : '-'}
                            </div>
                        </div>
                        <div className="ml-10 flex items-center">
                            <PermissionButton
                                permissions={['UPDATE.BACK_OFFICE_SETTINGS']}
                                variant="outlined"
                                size="small"
                                className="h-8"
                                onClick={openAutoblockCriteriaEditModal}
                            >
                                <FormattedMessage id="edit" />
                            </PermissionButton>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DebtCollection;

const mapReminderIntervalsToDto = (
    tenantConfiguration: TenantConfigurationDto,
    data: ReminderIntervalsForm
): DebtCollectionUpdateDto => {
    return {
        debtCollectionReminderIntervals: data.debtCollectionReminderIntervals,
        matterBlockingDebtClientThresholdAmount: tenantConfiguration.matterBlockingDebtClientThresholdAmount ?? null,
        matterBlockingGracePeriodDays: tenantConfiguration.matterBlockingGracePeriodDays,
        matterBlockingWarningPeriodDays: tenantConfiguration.matterBlockingWarningPeriodDays,
    };
};

const mapAutoblockCriteriaToDto = (
    tenantConfiguration: TenantConfigurationDto,
    data: AutoblockCriteriaForm
): DebtCollectionUpdateDto => {
    return {
        debtCollectionReminderIntervals: tenantConfiguration.debtCollectionReminderIntervals,
        matterBlockingDebtClientThresholdAmount: data.matterBlockingDebtClientThresholdAmount,
        matterBlockingGracePeriodDays: data.matterBlockingGracePeriodDays,
        matterBlockingWarningPeriodDays: data.matterBlockingWarningPeriodDays,
    };
};
